import React, { useState, useRef, useEffect } from 'react';
import { useCrypto } from '../contexts/CryptoContext';
import { useAuth } from '../contexts/AuthContext';

const IconLock = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

const IconEye = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconEyeOff = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

const IconSpinner = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83">
      <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite"/>
    </path>
  </svg>
);

export default function UnlockVaultModal() {
  const { unlockVault } = useCrypto();
  const { user, logout } = useAuth();

  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || loading) return;

    setError('');
    setLoading(true);

    try {
      await unlockVault(password, user.id);
      setPassword('');
    } catch (err) {
      setError(err.message || 'Impossible de déverrouiller le coffre.');
      setPassword('');
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>

        <div style={styles.header}>
          <div style={styles.iconContainer}>
            <IconLock />
          </div>
          <h1 style={styles.title}>Coffre verrouillé</h1>
          <p style={styles.subtitle}>
            Votre session a expiré ou la page a été rechargée.
            <br />
            Saisissez votre mot de passe pour accéder à votre coffre.
          </p>
        </div>

        {user?.email && (
          <div style={styles.emailBadge}>
            {user.email}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form} noValidate>
          <div style={styles.fieldWrapper}>
            <label htmlFor="unlock-password" style={styles.label}>
              Mot de passe
            </label>
            <div style={styles.inputRow}>
              <input
                id="unlock-password"
                name="unlock-password"
                ref={inputRef}
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  setError('');
                }}
                placeholder="Votre mot de passe SafeHeir"
                autoComplete="current-password"
                disabled={loading}
                style={{
                  ...styles.input,
                  borderColor: error ? '#FF4D4F' : 'rgba(0,245,160,0.2)',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPwd(v => !v)}
                style={styles.eyeBtn}
                aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                tabIndex={-1}
              >
                {showPwd ? <IconEyeOff /> : <IconEye />}
              </button>
            </div>

            {error && (
              <p style={styles.errorText} role="alert">
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={!password || loading}
            style={{
              ...styles.submitBtn,
              opacity: (!password || loading) ? 0.5 : 1,
              cursor: (!password || loading) ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? (
              <span style={styles.btnContent}>
                <IconSpinner />
                Déverrouillage en cours...
              </span>
            ) : (
              'Déverrouiller le coffre'
            )}
          </button>
        </form>

        <div style={styles.footer}>
          <span style={styles.footerText}>Ce n'est pas votre compte ?</span>
          <button
            type="button"
            onClick={logout}
            style={styles.logoutBtn}
            disabled={loading}
          >
            Se déconnecter
          </button>
        </div>

        <p style={styles.securityNote}>
          Votre mot de passe n'est jamais transmis à nos serveurs.
          Le déchiffrement s'effectue exclusivement sur votre appareil.
        </p>

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: '#0A1628',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
  },
  card: {
    width: '100%',
    maxWidth: '420px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(0,245,160,0.15)',
    borderRadius: '16px',
    padding: '40px 32px',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '24px',
  },
  iconContainer: {
    width: '56px',
    height: '56px',
    borderRadius: '12px',
    backgroundColor: 'rgba(0,245,160,0.1)',
    border: '1px solid rgba(0,245,160,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#00F5A0',
    marginBottom: '16px',
  },
  title: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#FFFFFF',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.55)',
    lineHeight: '1.5',
    margin: 0,
  },
  emailBadge: {
    backgroundColor: 'rgba(0,245,160,0.08)',
    border: '1px solid rgba(0,245,160,0.15)',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '13px',
    color: '#00F5A0',
    textAlign: 'center',
    marginBottom: '24px',
    fontFamily: 'monospace',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: 'rgba(255,255,255,0.7)',
  },
  inputRow: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(0,245,160,0.2)',
    borderRadius: '10px',
    padding: '12px 44px 12px 14px',
    fontSize: '15px',
    color: '#FFFFFF',
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
  },
  eyeBtn: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'rgba(255,255,255,0.4)',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    transition: 'color 0.15s',
  },
  errorText: {
    fontSize: '13px',
    color: '#FF4D4F',
    margin: '4px 0 0 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  submitBtn: {
    backgroundColor: '#00F5A0',
    color: '#0A1628',
    border: 'none',
    borderRadius: '10px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '700',
    width: '100%',
    transition: 'opacity 0.2s',
    fontFamily: 'inherit',
  },
  btnContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    marginTop: '20px',
  },
  footerText: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.4)',
  },
  logoutBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.6)',
    cursor: 'pointer',
    fontSize: '13px',
    textDecoration: 'underline',
    textDecorationColor: 'rgba(255,255,255,0.3)',
    fontFamily: 'inherit',
    padding: 0,
    transition: 'color 0.15s',
  },
  securityNote: {
    fontSize: '11px',
    color: 'rgba(255,255,255,0.25)',
    textAlign: 'center',
    marginTop: '20px',
    lineHeight: '1.6',
  },
};
